import { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=Source+Sans+3:wght@400;600;700&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Source Sans 3', 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
    line-height: 1.6;
    overflow-x: hidden;
  }

  img, video, canvas {
    max-width: 100%;
    height: auto;
  }

  p, li, h1, h2, h3, h4, h5, label, td, th, span {
    overflow-wrap: break-word;
  }

  h1, h2, h3 {
    font-family: 'Fraunces', Georgia, 'Times New Roman', serif;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }

  /* Responsive Typography */
  h1 {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    line-height: 1.2;
  }

  h2 {
    font-size: clamp(1.25rem, 3vw, 2rem);
    line-height: 1.3;
  }

  h3 {
    font-size: clamp(1.1rem, 2.5vw, 1.5rem);
    line-height: 1.4;
  }

  h4 {
    font-size: clamp(1rem, 2vw, 1.25rem);
    line-height: 1.4;
  }

  p {
    font-size: clamp(0.875rem, 1.5vw, 1rem);
    line-height: 1.6;
  }

  /* Responsive Container */
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
    
    @media (max-width: 768px) {
      padding: 0 0.75rem;
    }
    
    @media (max-width: 480px) {
      padding: 0 0.5rem;
    }
  }

  /* Responsive Grid System */
  .grid {
    display: grid;
    gap: 1rem;
  }

  .grid-1 { grid-template-columns: 1fr; }
  .grid-2 { grid-template-columns: repeat(2, 1fr); }
  .grid-3 { grid-template-columns: repeat(3, 1fr); }
  .grid-4 { grid-template-columns: repeat(4, 1fr); }

  @media (max-width: 1024px) {
    .grid-4 { grid-template-columns: repeat(3, 1fr); }
  }

  @media (max-width: 768px) {
    .grid-3, .grid-4 { grid-template-columns: repeat(2, 1fr); }
  }

  @media (max-width: 480px) {
    .grid-2, .grid-3, .grid-4 { grid-template-columns: 1fr; }
  }

  /* Responsive Flexbox */
  .flex {
    display: flex;
  }

  .flex-col {
    flex-direction: column;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }

  .items-center {
    align-items: center;
  }

  .justify-center {
    justify-content: center;
  }

  .justify-between {
    justify-content: space-between;
  }

  /* Responsive Spacing */
  .p-1 { padding: 0.25rem; }
  .p-2 { padding: 0.5rem; }
  .p-3 { padding: 0.75rem; }
  .p-4 { padding: 1rem; }
  .p-5 { padding: 1.25rem; }
  .p-6 { padding: 1.5rem; }

  .m-1 { margin: 0.25rem; }
  .m-2 { margin: 0.5rem; }
  .m-3 { margin: 0.75rem; }
  .m-4 { margin: 1rem; }
  .m-5 { margin: 1.25rem; }
  .m-6 { margin: 1.5rem; }

  @media (max-width: 768px) {
    .p-4 { padding: 0.75rem; }
    .p-5 { padding: 1rem; }
    .p-6 { padding: 1.25rem; }
    
    .m-4 { margin: 0.75rem; }
    .m-5 { margin: 1rem; }
    .m-6 { margin: 1.25rem; }
  }

  @media (max-width: 480px) {
    .p-4 { padding: 0.5rem; }
    .p-5 { padding: 0.75rem; }
    .p-6 { padding: 1rem; }
    
    .m-4 { margin: 0.5rem; }
    .m-5 { margin: 0.75rem; }
    .m-6 { margin: 1rem; }
  }

  /* Responsive Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
    border: none;
    font-size: 1rem;
    white-space: nowrap;
    
    @media (max-width: 768px) {
      padding: 0.625rem 1.25rem;
      font-size: 0.875rem;
    }
    
    @media (max-width: 480px) {
      padding: 0.5rem 1rem;
      font-size: 0.8rem;
      width: 100%;
    }
  }

  .btn-primary {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }

  .btn-primary:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }

  .btn-secondary {
    background-color: ${props => props.theme.colors.secondary};
    color: white;
  }

  .btn-secondary:hover {
    background-color: ${props => props.theme.colors.secondaryDark};
  }

  /* Responsive Cards */
  .card {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    
    @media (max-width: 768px) {
      padding: 1rem;
      border-radius: 0.5rem;
    }
    
    @media (max-width: 480px) {
      padding: 0.75rem;
      border-radius: 0.375rem;
    }
  }

  /* Responsive Forms */
  .form-group {
    margin-bottom: 1rem;
  }

  .form-label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: ${props => props.theme.colors.text};
    font-size: 0.875rem;
    
    @media (max-width: 480px) {
      font-size: 0.8rem;
    }
  }

  .form-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 0.5rem;
    font-size: 1rem;
    transition: border-color 0.2s ease;
    
    @media (max-width: 768px) {
      padding: 0.625rem;
      font-size: 0.875rem;
    }
    
    @media (max-width: 480px) {
      padding: 0.5rem;
      font-size: 0.8rem;
    }
  }

  .form-input:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.18);
  }

  /* Responsive Tables — wrap wide tables in .table-scroll (or TableScroll component) */
  .table-scroll {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
  }

  .table-scroll > table {
    min-width: 640px;
    width: 100%;
  }

  .table {
    width: 100%;
    overflow-x: auto;
    display: block;
    
    @media (min-width: 768px) {
      display: table;
    }
  }

  .table th,
  .table td {
    padding: 0.75rem;
    
    @media (max-width: 768px) {
      padding: 0.5rem;
      font-size: 0.875rem;
    }
    
    @media (max-width: 480px) {
      padding: 0.375rem;
      font-size: 0.8rem;
    }
  }

  /* Responsive Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Responsive Navigation */
  .nav-menu {
    @media (max-width: 768px) {
      position: fixed;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100vh;
      background: white;
      transition: left 0.3s ease;
      z-index: 1000;
    }
  }

  .nav-menu.active {
    @media (max-width: 768px) {
      left: 0;
    }
  }

  /* Responsive Sidebar */
  .sidebar {
    @media (max-width: 1024px) {
      position: fixed;
      left: -100%;
      transition: left 0.3s ease;
    }
  }

  .sidebar.active {
    @media (max-width: 1024px) {
      left: 0;
    }
  }

  /* Responsive Modal */
  .modal {
    @media (max-width: 768px) {
      margin: 1rem;
      width: calc(100% - 2rem);
      max-height: calc(100vh - 2rem);
    }
    
    @media (max-width: 480px) {
      margin: 0.5rem;
      width: calc(100% - 1rem);
      max-height: calc(100vh - 1rem);
    }
  }

  /* Responsive Charts */
  .chart-container {
    @media (max-width: 768px) {
      height: 300px;
    }
    
    @media (max-width: 480px) {
      height: 250px;
    }
  }

  /* Responsive Text */
  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  @media (max-width: 768px) {
    .text-center-mobile {
      text-align: center;
    }
  }

  /* Responsive Visibility */
  .hidden-mobile {
    @media (max-width: 768px) {
      display: none;
    }
  }

  .visible-mobile {
    @media (min-width: 769px) {
      display: none;
    }
  }

  .hidden-tablet {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  .visible-tablet {
    @media (min-width: 1025px) {
      display: none;
    }
  }

  /* Responsive Breakpoints */
  .mobile-only {
    @media (min-width: 769px) {
      display: none;
    }
  }

  .desktop-only {
    @media (max-width: 768px) {
      display: none;
    }
  }

  /* Responsive Utilities */
  .w-full { width: 100%; }
  .w-auto { width: auto; }
  
  .h-full { height: 100%; }
  .h-auto { height: auto; }
  
  .min-h-screen { min-height: 100vh; }
  
  .overflow-hidden { overflow: hidden; }
  .overflow-x-auto { overflow-x: auto; }
  .overflow-y-auto { overflow-y: auto; }

  /* Responsive Shadows */
  .shadow-sm {
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  }

  .shadow {
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  }

  .shadow-md {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  .shadow-lg {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  /* Responsive Borders */
  .border { border: 1px solid ${props => props.theme.colors.border}; }
  .border-0 { border: 0; }
  .border-t { border-top: 1px solid ${props => props.theme.colors.border}; }
  .border-b { border-bottom: 1px solid ${props => props.theme.colors.border}; }
  .border-l { border-left: 1px solid ${props => props.theme.colors.border}; }
  .border-r { border-right: 1px solid ${props => props.theme.colors.border}; }

  /* Responsive Rounded Corners */
  .rounded { border-radius: 0.375rem; }
  .rounded-sm { border-radius: 0.125rem; }
  .rounded-md { border-radius: 0.5rem; }
  .rounded-lg { border-radius: 0.75rem; }
  .rounded-xl { border-radius: 1rem; }
  .rounded-full { border-radius: 9999px; }

  /* Responsive Z-Index */
  .z-0 { z-index: 0; }
  .z-10 { z-index: 10; }
  .z-20 { z-index: 20; }
  .z-30 { z-index: 30; }
  .z-40 { z-index: 40; }
  .z-50 { z-index: 50; }

  /* Responsive Transitions */
  .transition { transition: all 0.15s ease-in-out; }
  .transition-colors { transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out; }
  .transition-opacity { transition: opacity 0.15s ease-in-out; }
  .transition-transform { transition: transform 0.15s ease-in-out; }

  /* Responsive Hover Effects */
  @media (hover: hover) {
    .hover\:scale-105:hover {
      transform: scale(1.05);
    }
    
    .hover\:shadow-lg:hover {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    }
  }

  /* Touch Device Optimizations */
  @media (hover: none) and (pointer: coarse) {
    .touch\:scale-105:active {
      transform: scale(1.05);
    }
  }

  /* Print Styles */
  @media print {
    .no-print {
      display: none;
    }
    
    body {
      background: white;
      color: black;
    }
  }
`;

export default GlobalStyle; 